import { t, Trans } from '@lingui/macro'
import { ErrorMessage } from 'formik'
import React from 'react'
import { ErrorLabel } from '../common-components/forms/ErrorLabel'
import SurveyChoiceQuestionPdf from './pdfSurveyComponents/SurveyChoiceQuestionPdf'
import SurveyHeaderPdf from './pdfSurveyComponents/SurveyHeaderPdf'
import SurveyRangeQuestionPdf from './pdfSurveyComponents/SurveyRangeQuestionPdf'
import SurveyTextInputQuestionPdf from './pdfSurveyComponents/SurveyTextInputQuestionPdf'
import {
  NumericFieldScoreConfig,
  SelectOptionScoreConfig,
  TextFieldScoreConfig
} from './ScoreCardCreator'
import {
  QuestionDate,
  QuestionDateRange,
  QuestionInputText,
  QuestionMultipleValue,
  QuestionNumericSlider,
  QuestionPointsGenerator,
  QuestionSelectOne,
  QuestionUploadFiles
} from './surveyQustions'

const fixedQuestionAnswers = {
  SELECT_YESNO: [t`Yes`, t`No`]
}

const questionTypes = {
  HEADER: {
    id: 'HEADER',
    text: <Trans>Header</Trans>,
    icon: 'title',
    class: () => '',
    pdfComponent: SurveyHeaderPdf
  },
  UPLOAD_FILES: {
    id: 'UPLOAD_FILES',
    text: <Trans>Question with file upload</Trans>,
    icon: 'file_upload',
    class: QuestionUploadFiles
  },
  SELECT_YESNO: {
    id: 'SELECT_YESNO',
    text: <Trans>Question with yes/no answer</Trans>,
    icon: 'thumbs_up_down',
    class: QuestionPointsGenerator,
    nonApplicableAsOption: true,
    pdfComponent: SurveyChoiceQuestionPdf
  },
  SELECT_ONE: {
    id: 'SELECT_ONE',
    text: <Trans>Question with custom, single answer</Trans>,
    icon: 'more_vert',
    class: QuestionSelectOne,
    scoreConfig: SelectOptionScoreConfig,
    nonApplicableAsOption: true,
    pdfComponent: SurveyChoiceQuestionPdf
  },
  SELECT_MULTIPLE: {
    id: 'SELECT_MULTIPLE',
    text: <Trans>Question with custom, multiple answers</Trans>,
    icon: 'more_vert',
    class: QuestionMultipleValue,
    scoreConfig: SelectOptionScoreConfig,
    pdfComponent: SurveyChoiceQuestionPdf
  },
  INPUT_DATE: {
    id: 'INPUT_DATE',
    text: <Trans>Question with date answer</Trans>,
    icon: 'calendar_today',
    class: QuestionDate,
    pdfComponent: SurveyTextInputQuestionPdf
  },
  INPUT_DATE_RANGE: {
    id: 'INPUT_DATE_RANGE',
    text: <Trans>Question with date range answer</Trans>,
    icon: 'date_range',
    class: QuestionDateRange,
    pdfComponent: SurveyRangeQuestionPdf
  },
  INPUT_TEXT: {
    id: 'INPUT_TEXT',
    text: <Trans>Question with short text answer</Trans>,
    icon: 'text_snippet',
    class: QuestionInputText,
    scoreConfig: TextFieldScoreConfig,
    pdfComponent: SurveyTextInputQuestionPdf
  },
  INPUT_TEXTAREA: {
    id: 'INPUT_TEXTAREA',
    text: <Trans>Question with long text answer</Trans>,
    icon: 'text_snippet',
    class: QuestionInputText,
    scoreConfig: TextFieldScoreConfig,
    pdfComponent: SurveyTextInputQuestionPdf
  },
  INPUT_NUMBER: {
    id: 'INPUT_NUMBER',
    text: <Trans>Question with numeric answer</Trans>,
    icon: 'looks_one',
    class: QuestionInputText,
    scoreConfig: NumericFieldScoreConfig,
    pdfComponent: SurveyTextInputQuestionPdf
  },
  SLIDER: {
    id: 'SLIDER',
    text: <Trans>Question with numeric slider</Trans>,
    icon: 'linear_scale',
    class: QuestionNumericSlider,
    scoreConfig: NumericFieldScoreConfig,
    pdfComponent: SurveyTextInputQuestionPdf
  }
}

const indexToFieldId = index => `field_${index}`
const indexToOption = index => `option_${index}`

const QuestionWidgetInner = props => {
  const {
    question,
    disabled,
    hideError,
    disableFields,
    index,
    errorIsArray,
    // className,
    version,
    questionValue,
    setFieldValue,
    surveyId,
    opportunity,
    entityFiles,
    network,
    i18n
  } = props

  const QuestionClass = questionTypes[question.type].class
  if (question.type === questionTypes.HEADER.id) {
    return null
  }
  const noNativeErrorClasses = [
    'SELECT_YESNO',
    'SELECT_ONE',
    'SELECT_MULTIPLE',
    'SLIDER'
  ]
  const noError = disabled || hideError
  const noNativeError = Boolean(
    noNativeErrorClasses.includes(question.type) && !question.isGroup
  )
  const mainErrorForGroup = Boolean(question.isGroup && !errorIsArray)
  return (
    <>
      {QuestionClass && (
        <>
          <QuestionClass
            hideError={noError}
            surveyId={surveyId}
            network={network}
            entityFiles={entityFiles}
            opportunity={opportunity}
            disabled={disabled}
            nonApplicableDisabled={props.nonApplicableDisabled}
            nonApplicable={props.nonApplicable}
            completedSurveys={null} // disable completed surveys and with it prefil
            version={version}
            disableFields={disableFields}
            questionValue={questionValue}
            setFieldValue={setFieldValue}
            question={question}
            fieldId={index}
            i18n={i18n}
          />
          {Boolean(noNativeError || mainErrorForGroup) && !noError && (
            <ErrorMessage name={index}>
              {msg => (
                <ErrorLabel label={msg} />
              )}
            </ErrorMessage>
          )}
        </>
      )}
    </>
  )
}

const QuestionWidget = React.memo(QuestionWidgetInner)

export {
  QuestionWidget,
  fixedQuestionAnswers,
  indexToFieldId,
  indexToOption,
  questionTypes
}
